/**
 * Table — 데이터 테이블
 *
 * 정렬된 데이터를 행/열로 표시한다. tokens.css 변수 사용.
 * 제네릭 <T>로 타입 안전한 컬럼 정의 + 정렬/로딩/행클릭 지원.
 */

import { Fragment, ReactNode } from 'react'
import { cn } from './cn'
import { Skeleton } from './Skeleton'
import styles from './Table.module.css'

export interface TableColumn<T = Record<string, ReactNode>> {
  key: string
  label: ReactNode
  width?: string
  align?: 'left' | 'center' | 'right'
  /** 커스텀 렌더러. 없으면 row[key]를 문자열로 표시 */
  render?: (row: T, index: number) => ReactNode
  /** 정렬 가능 여부 */
  sortable?: boolean
}

interface TableProps<T = Record<string, ReactNode>> {
  /** 테이블 컬럼 정의 */
  columns: TableColumn<T>[]
  /** 행 데이터 배열 */
  data: T[]
  /** 테이블 크기 */
  size?: 'sm' | 'md'
  /** 짝수 행 배경 강조 */
  striped?: boolean
  /** 행 hover 강조 */
  hoverable?: boolean
  /** 데이터 없을 때 표시할 메시지 */
  emptyMessage?: string
  className?: string
  /** 행 고유 키 추출 함수 */
  rowKey?: (row: T) => string
  /** 현재 정렬 컬럼 */
  sortBy?: string
  /** 정렬 방향 */
  sortOrder?: 'asc' | 'desc'
  /** 정렬 변경 콜백 (key + 계산된 방향) */
  onSort?: (key: string, order: 'asc' | 'desc') => void
  /** 로딩 상태 */
  isLoading?: boolean
  /** 로딩 시 스켈레톤 행 수 */
  loadingRows?: number
  /** 행 클릭 핸들러 */
  onRowClick?: (row: T) => void
  /** 각 행 다음에 추가 행을 렌더링하는 함수 (확장 상세 등) */
  renderAfterRow?: (row: T, index: number) => ReactNode
}

const alignClass: Record<string, string> = {
  left: styles.alignLeft,
  center: styles.alignCenter,
  right: styles.alignRight,
}

function SortIcon({ active, order }: { active: boolean; order: 'asc' | 'desc' }) {
  if (!active) return <span className={styles.sortIcon}>↕</span>
  return <span className={cn(styles.sortIcon, styles.sortActive)}>{order === 'asc' ? '↑' : '↓'}</span>
}

export function Table<T = Record<string, ReactNode>>({
  columns,
  data,
  size = 'md',
  striped = false,
  hoverable = true,
  emptyMessage = '데이터가 없습니다',
  className,
  rowKey,
  sortBy,
  sortOrder = 'desc',
  onSort,
  isLoading = false,
  loadingRows = 5,
  onRowClick,
  renderAfterRow,
}: TableProps<T>) {
  return (
    <div className={styles.wrapper}>
      <table
        className={cn(
          styles.root,
          styles[size],
          striped && styles.striped,
          hoverable && styles.hoverable,
          className,
        )}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  styles.th,
                  col.align ? alignClass[col.align] : styles.alignLeft,
                  col.sortable && styles.thSortable,
                )}
                style={col.width ? { width: col.width } : undefined}
                onClick={col.sortable && onSort ? () => {
                  const nextOrder = sortBy === col.key ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc'
                  onSort(col.key, nextOrder)
                } : undefined}
              >
                {col.label}
                {col.sortable && <SortIcon active={sortBy === col.key} order={sortOrder} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: loadingRows }).map((_, i) => (
              <tr key={`skeleton-${i}`} className={styles.tr}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(styles.td, col.align ? alignClass[col.align] : styles.alignLeft)}
                  >
                    <Skeleton width="70%" height={16} />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => {
              const key = rowKey ? rowKey(row) : String(rowIndex)
              return (
                <Fragment key={key}>
                  <tr
                    className={cn(styles.tr, onRowClick && styles.trClickable)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(styles.td, col.align ? alignClass[col.align] : styles.alignLeft)}
                      >
                        {col.render
                          ? col.render(row, rowIndex)
                          : String((row as Record<string, unknown>)[col.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                  {renderAfterRow?.(row, rowIndex)}
                </Fragment>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
